import React from 'react';
import { ScrollView, View, Linking } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import styled from 'styled-components/native';
import { useRoute, RouteProp, useNavigation } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '@theme/ThemeProvider';
import { Text, Card, Button, AppBar, Banner } from '@ui';
import { RootStackParamList } from '@navigation/types';

const Container = styled(SafeAreaView)`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.background};
`;

const Content = styled(ScrollView)`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.base}px;
`;

const HeroCard = styled(Card)`
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xxl}px;
  margin-bottom: ${({ theme }) => theme.spacing.base}px;
`;

const IconCircle = styled.View`
  width: 80px;
  height: 80px;
  border-radius: 40px;
  background-color: ${({ theme }) => theme.colors.brandPrimary}15;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.base}px;
`;

const InfoCard = styled(Card)`
  margin-bottom: ${({ theme }) => theme.spacing.md}px;
`;

type DeepLinkRouteProp = RouteProp<RootStackParamList, 'DeepLink'>;

const DeepLinkScreen: React.FC = () => {
  const theme = useTheme();
  const route = useRoute<DeepLinkRouteProp>();
  const navigation = useNavigation();
  const { type, title } = route.params;

  const getIconName = (): string => {
    switch (type) {
      case 'benefits':
        return 'card-giftcard';
      case 'claims':
        return 'receipt';
      case 'payments':
        return 'payment';
      case 'wellness':
        return 'spa';
      default:
        return 'open-in-new';
    }
  };

  const getDescription = (): string => {
    switch (type) {
      case 'benefits':
        return 'Access your employee benefits, review coverage, and explore additional programs available to you.';
      case 'claims':
        return 'Submit new claims, track existing claims, and view your claim history.';
      case 'payments':
        return 'Manage your premium payments, view payment history, and update payment methods.';
      case 'wellness':
        return 'Explore wellness programs, book health screenings, and access fitness resources.';
      default:
        return 'This feature will redirect you to the AIA portal for secure access.';
    }
  };

  const handleOpenExternal = () => {
    // In a real app, this would open the actual deep link URL
    const urls: Record<string, string> = {
      benefits: 'https://benefits.aia.com.hk/employee-benefits',
      claims: 'https://claims.aia.com.hk',
      payments: 'https://payments.aia.com.hk',
      wellness: 'https://wellness.aia.com.hk',
    };

    const url = urls[type] || 'https://aia.com.hk';
    Linking.openURL(url);
  };

  return (
    <Container edges={['top']}>
      <AppBar title={title} leftIcon="close" onLeftPress={() => navigation.goBack()} />
      <Content showsVerticalScrollIndicator={false}>
        <HeroCard>
          <IconCircle>
            <Icon name={getIconName()} size={48} color={theme.colors.brandPrimary} />
          </IconCircle>
          <Text variant="headline" color="textPrimary" align="center">
            {title}
          </Text>
          <Text variant="body" color="textSecondary" align="center" style={{ marginTop: 12 }}>
            {getDescription()}
          </Text>
        </HeroCard>

        <InfoCard padding="md">
          <Text variant="titleMedium" color="textPrimary" style={{ marginBottom: 8 }}>
            What You Can Do
          </Text>
          {type === 'benefits' && (
            <>
              <Text variant="bodyMedium" color="textSecondary" style={{ marginBottom: 4 }}>
                • View your coverage details
              </Text>
              <Text variant="bodyMedium" color="textSecondary" style={{ marginBottom: 4 }}>
                • Download benefit summaries
              </Text>
              <Text variant="bodyMedium" color="textSecondary" style={{ marginBottom: 4 }}>
                • Enroll in additional benefits
              </Text>
            </>
          )}
          {type === 'claims' && (
            <>
              <Text variant="bodyMedium" color="textSecondary" style={{ marginBottom: 4 }}>
                • Submit medical claims
              </Text>
              <Text variant="bodyMedium" color="textSecondary" style={{ marginBottom: 4 }}>
                • Track claim status
              </Text>
              <Text variant="bodyMedium" color="textSecondary" style={{ marginBottom: 4 }}>
                • View reimbursement history
              </Text>
            </>
          )}
          {type === 'payments' && (
            <>
              <Text variant="bodyMedium" color="textSecondary" style={{ marginBottom: 4 }}>
                • Make premium payments
              </Text>
              <Text variant="bodyMedium" color="textSecondary" style={{ marginBottom: 4 }}>
                • Set up auto-pay
              </Text>
              <Text variant="bodyMedium" color="textSecondary" style={{ marginBottom: 4 }}>
                • Download payment receipts
              </Text>
            </>
          )}
          {type === 'wellness' && (
            <>
              <Text variant="bodyMedium" color="textSecondary" style={{ marginBottom: 4 }}>
                • Book health screenings
              </Text>
              <Text variant="bodyMedium" color="textSecondary" style={{ marginBottom: 4 }}>
                • Join wellness programs
              </Text>
              <Text variant="bodyMedium" color="textSecondary" style={{ marginBottom: 4 }}>
                • Access fitness resources
              </Text>
            </>
          )}
        </InfoCard>

        <Banner
          variant="info"
          message="You will be redirected to the AIA secure portal. We do not store or access your claim data."
        />

        <Button
          variant="primary"
          fullWidth
          iconRight="arrow-forward"
          onPress={handleOpenExternal}
          style={{ marginTop: 16 }}
        >
          Open in AIA Portal
        </Button>

        <Button
          variant="ghost"
          fullWidth
          onPress={() => navigation.goBack()}
          style={{ marginTop: 12 }}
        >
          Cancel
        </Button>

        <View style={{ height: 24 }} />
      </Content>
    </Container>
  );
};

export default DeepLinkScreen;
